import { randomChars } from "@/lib/random-string";

export const MCP_TAG_MAX = 12;
export const MCP_TAGS_MAX_COUNT = 5;

export function normalizeMcpTags(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of raw) {
    if (typeof item !== "string") {
      continue;
    }

    const text = item.trim().slice(0, MCP_TAG_MAX);

    if (!text || seen.has(text)) {
      continue;
    }

    seen.add(text);
    result.push(text);

    if (result.length >= MCP_TAGS_MAX_COUNT) {
      break;
    }
  }

  return result;
}

export const MCP_TRANSPORTS = ["http", "sse"] as const;

export type McpTransport = (typeof MCP_TRANSPORTS)[number];

export const MCP_TRANSPORT_LABELS: Record<McpTransport, string> = {
  http: "流式 HTTP",
  sse: "SSE（旧版）",
};

export const MCP_TRANSPORT_DESCRIPTIONS: Record<McpTransport, string> = {
  http: "MCP 当前推荐的传输方式，单个端点收发，多数服务器用这个",
  sse: "2024-11 版协议的旧式传输，仅在服务器只提供 SSE 端点时选它",
};

export const MCP_TRANSPORT_OPTIONS = MCP_TRANSPORTS.map((value) => ({
  value,
  label: MCP_TRANSPORT_LABELS[value],
}));

export const MCP_LIST_MODES = ["none", "listed"] as const;

export type McpListMode = (typeof MCP_LIST_MODES)[number];

export const MCP_LIST_MODE_LABELS: Record<McpListMode, string> = {
  none: "不上架",
  listed: "上架",
};

export const MCP_LIST_MODE_OPTIONS = MCP_LIST_MODES.map((value) => ({
  value,
  label: MCP_LIST_MODE_LABELS[value],
}));

export function isMcpListMode(value: unknown): value is McpListMode {
  return (
    typeof value === "string" && MCP_LIST_MODES.includes(value as McpListMode)
  );
}

export function resolveMcpToolPrice(toolPrice: number | null | undefined): number {
  return toolPrice ?? 0;
}

export function mcpToolPricing(tools: readonly { callPrice: number | null }[]) {
  const prices = tools.map(tool => resolveMcpToolPrice(tool.callPrice));
  return { price: prices.length ? Math.min(...prices) : 0, maxPrice: prices.length ? Math.max(...prices) : 0 };
}

export function mcpPriceLabel(price: number, maxPrice = price): string {
  return maxPrice <= 0 ? "免费" : price === maxPrice ? `${price} 积分 / 次` : `${price}–${maxPrice} 积分 / 次`;
}

export const MCP_NEW_NODE_DEFAULT_PRICE = 0;

export const MCP_DEFAULT_GROUP_ID = "default";

export function isMcpDefaultGroupId(id: string) {
  return id === MCP_DEFAULT_GROUP_ID;
}

export const MCP_SERVER_MAX_TOOLS = 500;

export const MCP_TOOL_NAME_MAX_LENGTH = 128;

const MCP_CODE_ALPHABET =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const MCP_CODE_LENGTH = 12;
export const MCP_CODE_MAX_LENGTH = 32;

export function createMcpServerCode(): string {
  return randomChars(MCP_CODE_LENGTH, MCP_CODE_ALPHABET);
}

export function sanitizeMcpServerCode(raw: string): string {
  return raw.replace(/[^0-9A-Za-z]/g, "").slice(0, MCP_CODE_MAX_LENGTH);
}

export function createMcpEndpointCode(): string {
  return randomChars(MCP_ENDPOINT_CODE_LENGTH, MCP_CODE_ALPHABET);
}

export function shouldFailoverMcpEndpoint(message: string): boolean {
  return /429|too many requests|rate.?limit|quota|限流|频率|502|503|504|bad gateway|transport error|fetch failed|econnrefused|econnreset|enotfound|etimedout|socket hang up|network/i.test(
    message,
  );
}

export const MCP_MAX_AUTH_HEADERS = 10;

export const MCP_MAX_ENDPOINTS = 20;

const MCP_ENDPOINT_CODE_LENGTH = 8;

export const MCP_MAX_FAILOVER_ATTEMPTS = 3;

export const MCP_FAILOVER_BUDGET_MS = 30_000;

const MCP_HEADER_NAME_ALLOWED = /[^A-Za-z0-9!#$%&'*+.^_`|~-]/g;

const MCP_RESERVED_HEADER_NAMES = new Set([
  "host",
  "content-length",
  "content-type",
  "accept",
  "connection",
  "transfer-encoding",
  "mcp-session-id",
  "mcp-protocol-version",
]);

export function sanitizeMcpHeaderName(raw: string): string {
  return raw.replace(MCP_HEADER_NAME_ALLOWED, "").slice(0, 64);
}

export function isReservedMcpHeaderName(name: string): boolean {
  return MCP_RESERVED_HEADER_NAMES.has(name.trim().toLowerCase());
}

export function isMcpSecretHeaderName(name: string): boolean {
  return /secret|token|key|password|passwd|auth|credential|sign/i.test(name);
}

export function resolveMcpRateLimit(
  windowMinutes: number,
  maxCalls: number,
): { windowMs: number; maxCalls: number } | null {
  if (windowMinutes <= 0 || maxCalls <= 0) return null;

  return { windowMs: windowMinutes * 60_000, maxCalls };
}

export function mcpNodeGroupKey(toolName: string): string {
  const index = toolName.indexOf("_");

  return index > 0 ? toolName.slice(0, index + 1) : "其他";
}

export function isMcpTransport(value: unknown): value is McpTransport {
  return (
    typeof value === "string" && MCP_TRANSPORTS.includes(value as McpTransport)
  );
}

export function mcpTransportLabel(value: string): string {
  return isMcpTransport(value) ? MCP_TRANSPORT_LABELS[value] : value;
}

export function normalizeMcpUrl(raw: string): string {
  return raw.trim();
}

export function hasMcpUrlSecret(url: string) { return url.includes("?"); }
export function maskMcpUrlQuery(url: string) {
  const index = url.indexOf("?");
  return index < 0 ? url : `${url.slice(0, index)}?···`;
}
export function maskMcpUrlMessage(message: string) {
  return message.replace(/https?:\/\/[^\s"'<>]+/g, maskMcpUrlQuery);
}

export function resolveMaxCallsPerMessage(configured: number): number {
  return configured > 0 ? configured : Infinity;
}
